import {
    DeepIntelligenceConfigurationModel,
    ManagedAgentResponseModel,
    MgmtApi
} from '@/customApiClients/mgmtApiClient/Api';
import { createAutoMemoryStore } from '@/lib/autoMemoryStore';
import {
    ArrowLeft,
    Check,
    Ellipsis,
    FlaskConical,
    Loader2,
    Trash2,
    Zap
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { USER_TZ } from '@/components/DMAdminComponents/CronPicker';
import { MobileNavTrigger } from '@/components/ScheduledAgents/MobileNavTrigger';
import { StepBasics } from '@/components/ScheduledAgents/StepBasics';
import { StepContext } from '@/components/ScheduledAgents/StepContext';
import { StepInstructions } from '@/components/ScheduledAgents/StepInstructions';
import { StepPublish } from '@/components/ScheduledAgents/StepPublish';
import { StepSchedule } from '@/components/ScheduledAgents/StepSchedule';
import {
    WEEKDAYS,
    computeNextRuns,
    parseQuartzCron,
    toQuartzCron
} from '@/components/ScheduledAgents/scheduleUtils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import {
    Stepper,
    StepperIndicator,
    StepperItem,
    StepperNav,
    StepperSeparator,
    StepperTitle,
    StepperTrigger
} from '@/components/ui/stepper';
import { Switch } from '@/components/ui/switch';

import { useAuth } from '@/hooks/useAuth';

const STEPS = [
    { id: 'basics', title: 'Basics' },
    { id: 'context', title: 'Context & tools' },
    { id: 'instructions', title: 'Instructions' },
    { id: 'schedule', title: 'Schedule' },
    { id: 'publish', title: 'Publish' }
];

export default function ManageAgent() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const [step, setStep] = useState(0);

    const mgmtApi = useMemo(
        () =>
            new MgmtApi({
                baseUrl: '/mgmt',
                baseApiParams: { credentials: 'include' }
            }),
        []
    );

    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [managedAgents, setManagedAgents] = useState<
        ManagedAgentResponseModel[]
    >([]);

    // Form state — mirrors ScheduledAgentSetup's, field for field, so the two
    // pages stay visually and behaviorally identical.
    const [displayName, setDisplayName] = useState('');
    const [description, setDescription] = useState('');
    const [managedAgentIds, setManagedAgentIds] = useState<string[]>([]);
    const [useMemory, setUseMemory] = useState(false);
    const [memoryStoreId, setMemoryStoreId] = useState('');
    const [memoryPath, setMemoryPath] = useState('');
    const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
    const [prompt, setPrompt] = useState('');
    const [format, setFormat] = useState('Article');
    const [depth, setDepth] = useState('Deep dive');
    const [length, setLength] = useState('Standard');
    const [frequency, setFrequency] = useState('Daily');
    const [weekdays, setWeekdays] = useState<Record<string, boolean>>(
        Object.fromEntries(WEEKDAYS.map((d, i) => [d, i === 0]))
    );
    const [monthday, setMonthday] = useState('1st');
    const [time, setTime] = useState('08:00');
    const [tz, setTz] = useState(USER_TZ);
    const [visibility, setVisibility] = useState<'private' | 'org'>('org');
    const [isActive, setIsActive] = useState(true);

    const requiresPrivate = managedAgents.some(
        (a) => managedAgentIds.includes(a.guid) && a.enableRls
    );

    useEffect(() => {
        if (requiresPrivate) setVisibility('private');
    }, [requiresPrivate]);

    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const nextRuns = computeNextRuns(frequency, weekdays, monthday, time);
    const toggleWeekday = (day: string) =>
        setWeekdays((prev) => ({ ...prev, [day]: !prev[day] }));

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        Promise.all([
            mgmtApi.api.v1DeepIntelligenceConfigurationList(),
            mgmtApi.api.v1ManagedAgentsAccessibleList()
        ])
            .then(([configsRes, agentsRes]) => {
                const configs: DeepIntelligenceConfigurationModel[] =
                    configsRes?.data || [];
                const config = configs.find((c) => c.id === id);
                setManagedAgents(agentsRes?.data?.data || []);
                if (!config) {
                    setNotFound(true);
                    return;
                }
                setDisplayName(config.displayName);
                setDescription(config.description ?? '');
                const parsed = parseQuartzCron(config.cronExpression);
                setFrequency(parsed.frequency);
                setWeekdays(parsed.weekdays);
                setMonthday(parsed.monthday);
                setTime(parsed.time);
                setPrompt(config.prompt);
                // Pre-existing single-agent configs saved before this field
                // existed fall back to their one agent.
                const selectedIdsJson = config.selectedManagedAgentIds;
                try {
                    const ids = selectedIdsJson
                        ? JSON.parse(selectedIdsJson)
                        : [];
                    setManagedAgentIds(
                        Array.isArray(ids) && ids.length > 0
                            ? ids
                            : [config.managedAgentId]
                    );
                } catch {
                    setManagedAgentIds([config.managedAgentId]);
                }
                setIsActive(config.isActive);
                setVisibility(config.isPublic ? 'org' : 'private');
                setTz(config.timeZone || USER_TZ);
                setMemoryStoreId(config.memoryStoreId ?? '');
                setMemoryPath(config.memoryPath ?? '');
                setUseMemory(!!config.memoryStoreId);
                const filesJson = config.files;
                try {
                    const ids = filesJson ? JSON.parse(filesJson) : [];
                    setSelectedFileIds(Array.isArray(ids) ? ids : []);
                } catch {
                    setSelectedFileIds([]);
                }
            })
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [id, mgmtApi]);

    const handleSave = async () => {
        if (!displayName || !prompt || managedAgentIds.length === 0) {
            toast.error('Name, agent, and prompt are required');
            return;
        }
        if (frequency === 'Weekly' && !Object.values(weekdays).some(Boolean)) {
            toast.error('Select at least one weekday');
            return;
        }
        setSaving(true);
        try {
            // Persistent memory is a plain yes/no toggle — the store and its
            // one fixed file are created behind the scenes here (named after
            // the job) the first time it's turned on; an existing store is
            // just reused, and turning it off clears the link without
            // deleting the underlying store.
            let finalMemoryStoreId = memoryStoreId || null;
            let finalMemoryPath = memoryPath || null;
            if (useMemory && !memoryStoreId) {
                const memory = await createAutoMemoryStore(
                    displayName,
                    mgmtApi
                );
                finalMemoryStoreId = memory.memoryStoreId;
                finalMemoryPath = memory.memoryPath;
            } else if (!useMemory) {
                finalMemoryStoreId = null;
                finalMemoryPath = null;
            }

            // Backfills OwnerDisplayName server-side if it's still null (e.g.
            // an older job saved before this field existed) — never
            // overwrites an already-recorded owner's name, see
            // DeepIntelligenceConfigurationRepository.UpdateAsync.
            const ownerMeta = (user?.metadata || {}) as Record<string, any>;
            const ownerDisplayName =
                [ownerMeta.given_name, ownerMeta.family_name]
                    .filter(Boolean)
                    .join(' ')
                    .trim() ||
                user?.identifier ||
                undefined;

            const cronExpression = toQuartzCron(
                frequency,
                weekdays,
                monthday,
                time
            );

            await mgmtApi.api.v1DeepIntelligenceConfigurationCreate({
                id,
                displayName,
                description: description || null,
                chatUserId: user?.id ?? '',
                ownerDisplayName,
                cronExpression,
                prompt,
                managedAgentIds,
                isActive,
                isPublic: !requiresPrivate && visibility === 'org',
                timeZone: tz,
                memoryStoreId: finalMemoryStoreId,
                memoryPath: finalMemoryPath,
                files: selectedFileIds.length
                    ? JSON.stringify(selectedFileIds)
                    : null
            });
            setMemoryStoreId(finalMemoryStoreId || '');
            setMemoryPath(finalMemoryPath || '');
            toast.success('Saved successfully!');
        } catch (e: any) {
            console.error('Save failed', e);
            toast.error(
                e?.error?.detail ||
                    e?.error?.title ||
                    e?.message ||
                    (e?.status
                        ? `Request failed (${e.status} ${e.statusText ?? ''})`
                        : 'Failed to save')
            );
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        if (!id) return;
        setTesting(true);
        try {
            await mgmtApi.api.v1DeepIntelligenceConfigurationTestCreate(id);
            toast.success('Run triggered — check your feed shortly.');
        } catch (e: any) {
            console.error('Run now failed', e);
            toast.error(
                e?.error?.detail ||
                    e?.error?.title ||
                    e?.message ||
                    (e?.status
                        ? `Request failed (${e.status} ${e.statusText ?? ''})`
                        : 'Failed to run')
            );
        } finally {
            setTesting(false);
        }
    };

    const handleDelete = async () => {
        if (!id) return;
        setDeleteDialogOpen(false);
        setSaving(true);
        try {
            // Cascade-delete the job's auto-created memory store, if any — it's
            // 1:1 with this job (named after it at creation time, see
            // createAutoMemoryStore), so nothing else references it. Best-effort:
            // a failure here shouldn't block deleting the job config itself.
            if (memoryStoreId) {
                try {
                    const storesRes =
                        await mgmtApi.api.v1ClaudeMemoryStoresList();
                    const match = (storesRes?.data || []).find(
                        (s) => s.anthropicMemoryStoreId === memoryStoreId
                    );
                    await fetch(`/api/memory-stores/${memoryStoreId}`, {
                        method: 'DELETE',
                        credentials: 'include'
                    });
                    if (match) {
                        await mgmtApi.api.v1ClaudeMemoryStoresDelete(match.id);
                    }
                } catch (e) {
                    console.error(
                        'Failed to delete associated memory store',
                        e
                    );
                }
            }

            await mgmtApi.api.v1DeepIntelligenceConfigurationDelete(id);
            toast.success('Job deleted.');
            navigate('/scheduled-jobs/bank');
        } catch (e: any) {
            console.error('Delete failed', e);
            toast.error(
                e?.error?.detail ||
                    e?.error?.title ||
                    e?.message ||
                    (e?.status
                        ? `Request failed (${e.status} ${e.statusText ?? ''})`
                        : 'Failed to delete')
            );
            setSaving(false);
        }
    };

    const isLastStep = step === STEPS.length - 1;

    if (loading) {
        return (
            <div className="flex justify-center items-center bg-background w-full h-full">
                <div className="flex items-center gap-3 text-muted-foreground">
                    <Loader2 className="size-5 animate-spin" />
                    <span className="text-lg">Loading...</span>
                </div>
            </div>
        );
    }

    if (notFound) {
        return (
            <div className="flex flex-col justify-center items-center gap-4 bg-background w-full h-full">
                <p className="text-muted-foreground">
                    Configuration not found.
                </p>
                <Button
                    variant="tertiary"
                    onClick={() => navigate('/scheduled-jobs/bank')}
                >
                    <ArrowLeft />
                    Back to bank
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col bg-background w-full h-full">
            <MobileNavTrigger />
            {/* Back to bank */}
            <div className="top-0 z-10 sticky bg-background px-4 sm:px-6 lg:px-10 shrink-0">
                <div className="mx-auto pt-6 max-w-5xl">
                    <Button
                        onClick={() => navigate('/scheduled-jobs/bank')}
                        variant="ghost"
                        className="group justify-start -ml-3 px-3 w-fit text-foreground hover:text-foreground"
                    >
                        <span className="relative w-4 h-4 overflow-hidden">
                            <ArrowLeft className="absolute inset-0 transition-transform group-hover:-translate-x-full duration-300 ease-out" />
                            <ArrowLeft className="absolute inset-0 transition-transform translate-x-full group-hover:translate-x-0 duration-300 ease-out" />
                        </span>
                        Back to bank
                    </Button>
                </div>
            </div>

            {/* Header */}
            <div className="px-4 sm:px-6 lg:px-10 border-border border-b shrink-0">
                <div className="flex justify-between items-start gap-4 mx-auto mb-4 py-4 max-w-5xl">
                    <div>
                        <h1 className="font-medium text-foreground text-2xl lg:text-3xl">
                            Manage {displayName || 'job'}
                        </h1>
                    </div>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="tertiary"
                                size="icon"
                                className="shrink-0"
                            >
                                <Ellipsis />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem
                                onSelect={handleTest}
                                disabled={testing || saving}
                            >
                                {testing ? (
                                    <Loader2 className="animate-spin" />
                                ) : (
                                    <FlaskConical />
                                )}
                                Run now
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                variant="destructive"
                                disabled={saving}
                                onSelect={(e) => {
                                    e.preventDefault();
                                    setDeleteDialogOpen(true);
                                }}
                            >
                                <Trash2 />
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            <AlertDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Delete configuration?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {`Are you sure you want to delete "${displayName}"? This action cannot be undone.`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={handleDelete}>
                                Delete
                            </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Step rail */}
            <div className="py-5 pt-8 border-border border-b shrink-0">
                <Stepper
                    steps={STEPS}
                    className="flex items-center mx-auto px-4 max-w-agentPageWidth"
                    value={STEPS[step].id}
                    onValueChange={(id) => {
                        const index = STEPS.findIndex((s) => s.id === id);
                        if (index !== -1) setStep(index);
                    }}
                >
                    <StepperNav>
                        {STEPS.map((s, i) => (
                            <StepperItem
                                key={s.id}
                                stepId={s.id}
                                className="relative flex-1 min-w-0"
                            >
                                <StepperTrigger className="flex-col gap-2.5">
                                    <StepperIndicator>{i + 1}</StepperIndicator>
                                    <StepperTitle>{s.title}</StepperTitle>
                                </StepperTrigger>
                                {i < STEPS.length - 1 && (
                                    <StepperSeparator className="top-2 right-[calc(-50%+1rem)] left-[calc(50%+1rem)] absolute" />
                                )}
                            </StepperItem>
                        ))}
                    </StepperNav>
                </Stepper>
            </div>

            {/* Scrollable step content */}
            <div className="flex-1 px-4 sm:px-6 lg:px-10 overflow-y-auto">
                <div className="mx-auto py-10 max-w-5xl">
                    {step === 0 && (
                        <StepBasics
                            name={displayName}
                            setName={setDisplayName}
                            description={description}
                            setDescription={setDescription}
                        />
                    )}
                    {step === 1 && (
                        <StepContext
                            managedAgents={managedAgents}
                            managedAgentIds={managedAgentIds}
                            setManagedAgentIds={setManagedAgentIds}
                            useMemory={useMemory}
                            setUseMemory={setUseMemory}
                            selectedFileIds={selectedFileIds}
                            setSelectedFileIds={setSelectedFileIds}
                        />
                    )}
                    {step === 2 && (
                        <StepInstructions
                            prompt={prompt}
                            setPrompt={setPrompt}
                            format={format}
                            setFormat={setFormat}
                            depth={depth}
                            setDepth={setDepth}
                            length={length}
                            setLength={setLength}
                        />
                    )}
                    {step === 3 && (
                        <StepSchedule
                            frequency={frequency}
                            setFrequency={setFrequency}
                            weekdays={weekdays}
                            toggleWeekday={toggleWeekday}
                            monthday={monthday}
                            setMonthday={setMonthday}
                            time={time}
                            setTime={setTime}
                            tz={tz}
                            setTz={setTz}
                            nextRuns={nextRuns}
                        />
                    )}
                    {step === 4 && (
                        <div className="space-y-8">
                            <StepPublish
                                visibility={visibility}
                                setVisibility={setVisibility}
                                agentName={displayName || 'job'}
                                selectedAgentName={
                                    managedAgents
                                        .filter((a) =>
                                            managedAgentIds.includes(a.guid)
                                        )
                                        .map((a) => a.displayName)
                                        .join(', ') || undefined
                                }
                                userLabel={user?.identifier ?? user?.id}
                                rlsEnabled={requiresPrivate}
                            />

                            {/* Active — manage-only, no equivalent on create
                                since a new job always starts active. */}
                            <div className="space-y-1.5">
                                <div className="border border-[#e9e8e4] dark:border-border rounded-lg overflow-hidden">
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={() =>
                                            setIsActive((prev) => !prev)
                                        }
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                            ) {
                                                e.preventDefault();
                                                setIsActive((prev) => !prev);
                                            }
                                        }}
                                        className="flex justify-between items-center gap-4 hover:bg-muted/50 p-4 transition-colors cursor-pointer"
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="flex justify-center items-center border border-border rounded-lg size-10 shrink-0">
                                                <Zap className="size-5" />
                                            </div>
                                            <div>
                                                <Label
                                                    htmlFor="isActive"
                                                    className="font-medium text-base"
                                                >
                                                    Active
                                                </Label>
                                                <p className="text-muted-foreground text-sm">
                                                    Runs automatically on its
                                                    schedule. Turn off to pause
                                                    without deleting the job.
                                                </p>
                                            </div>
                                        </div>
                                        <Switch
                                            id="isActive"
                                            checked={isActive}
                                            onCheckedChange={setIsActive}
                                            onClick={(e) => e.stopPropagation()}
                                            className="shrink-0"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step navigation — sits right under the step's own
                        content instead of a page-wide docked footer, so it
                        stays part of the same centered form group. */}
                    <div className="mt-8 pt-6 border-border border-t">
                        <div className="flex justify-between items-center gap-2">
                            <Button
                                variant="tertiary"
                                onClick={() =>
                                    setStep((s) => Math.max(s - 1, 0))
                                }
                                disabled={step === 0}
                                size={'default'}
                                className="px-4"
                            >
                                Back
                            </Button>

                            {isLastStep ? (
                                <div className="flex items-center gap-2">
                                    <Button
                                        onClick={handleSave}
                                        disabled={saving}
                                        size={'default'}
                                    >
                                        {saving ? (
                                            <>
                                                <Loader2 className="animate-spin" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <Check
                                                   
                                                    strokeWidth={2.5}
                                                />
                                                Save changes
                                            </>
                                        )}
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    onClick={() =>
                                        setStep((s) =>
                                            Math.min(s + 1, STEPS.length - 1)
                                        )
                                    }
                                    size={'default'}
                                >
                                    Continue
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
